"use client";

import { useEffect, useState } from "react";
import { Product, Variant, VariantGroup } from "../../types/types";
import { ColoursUI } from "./ColoursUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { hasSizes } from "../../utils/product/hasSizes";
import { createVariantGroupUrlObj } from "./utils/createVariantGroupUrlObj.ts";
import { useRouter } from "next/router";
import {
  isVariantReducedToClear,
  isVariantPromotional,
  isAllVariantsReduced,
  isAllVariantsSave,
  isAllVariantsReducedForColour,
  isSomeVariantsReducedForColour,
  isAllVariantsSaveForColour,
  isSomeVariantsSaveForColour,
} from "../../utils/product/promotionUtils";
import { ColourOption } from "@/components/Colours/types.ts";
import {
  recordImpressionByParam,
  sendEvent,
} from "@JohnLewisPartnership/experiments-utils";
import { COLOUR_SELECTOR_SHOW_MORE_BUTTON_CLICK } from "@/components/Colours/ColourList/monetateEvents.ts";

const isMobileWidth = () => window.innerWidth < 768;

export const Colours = ({
  configProps,
  flags,
  product,
  runtimeConfig,
  experiencesRawObject,
}: Pick<
  StandardProps,
  "product" | "flags" | "configProps" | "runtimeConfig" | "experiencesRawObject"
>) => {
  const { variant } = useVariant(product);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();
  const { brand } = product;

  const promotionIconVariant = flags.promotionIconVariant.value;
  const showPromoOnIndicators = !!flags.showPromoOnIndicators.value;
  const useSimplifiedColourSelector = flags.useSimplifiedColourSelector.value;
  const observeColourPromotion = flags.observeColourPromotion.value;

  const useSimplifiedVersion = Boolean(
    configProps?.useSimplifiedVersion || useSimplifiedColourSelector,
  );

  useEffect(() => {
    setIsMobile(isMobileWidth());
    const handleResize = () =>
      isMobileWidth() ? setIsMobile(true) : setIsMobile(false);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const variantGroupsHaveColours = !!product.variantGroups?.[0]?.value;

  const colourOptions: ColourOption[] =
    product.variantGroups?.map((variantGroup: VariantGroup) => {
      const colour = variantGroup.value || "";

      const groupVariants = getVariantsForGroup(variantGroup, product);
      const isAvailable = groupVariants.some(
        (v) =>
          v.availability?.availableToOrder ||
          v.availability?.unavailableReason === "inStoreOnly",
      );

      const { showReduced, label } = getColourReductionLabelInfo(
        product,
        groupVariants[0],
        variantGroup,
        showPromoOnIndicators,
        isAvailable,
      );

      return {
        colour,
        showAsAvailable: isAvailable,
        colourSwatch: groupVariants[0]?.colourSwatch,
        showReduced,
        reducedLabel: label,
        selected: variant.differentiators?.colour === colour,
        url: createVariantGroupUrlObj(variantGroup, router.query),
      };
    }) || [];

  const sortedColourOptions = sortColourOptions(colourOptions);

  const rowsToDisplay = getItemsCountToDisplay(
    isMobile,
    sortedColourOptions,
    variant,
  );

  const someVariantsInStock = sortedColourOptions.some(
    (colourOption) => colourOption.showAsAvailable,
  );
  const someVariantsOutOfStock = sortedColourOptions.some(
    (colourOption) => !colourOption.showAsAvailable,
  );

  const variantsContainSizes = hasSizes(product);

  const eligibleForPromotion = sortedColourOptions.some(
    (colourOption) => colourOption.showReduced,
  );

  useEffect(() => {
    if (
      someVariantsInStock &&
      someVariantsOutOfStock &&
      !variantsContainSizes &&
      eligibleForPromotion &&
      observeColourPromotion
    ) {
      fireManagedImpression();
    }
  }, [
    someVariantsInStock,
    someVariantsOutOfStock,
    variantsContainSizes,
    eligibleForPromotion,
    observeColourPromotion,
  ]);

  const fireManagedImpression = () => {
    recordImpressionByParam(
      runtimeConfig?.monetateEnvironment,
      experiencesRawObject as [],
      "pdp-scaffold-observeColourPromotion",
      "true",
    );
  };

  useEffect(() => {
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target) sendEvent(COLOUR_SELECTOR_SHOW_MORE_BUTTON_CLICK);
    };

    const colourContainer = document.querySelector(
      '[data-component="Colours"] > div > button',
    ) as HTMLElement;

    colourContainer?.addEventListener("click", handleClick);

    return () => {
      colourContainer?.removeEventListener("click", handleClick);
    };
  }, []);

  if (!variantGroupsHaveColours) return null;
  if (colourOptions?.length <= 1 && !useSimplifiedVersion) return null;

  return (
    <ColoursUI
      colourOptions={sortedColourOptions}
      rowsToDisplay={rowsToDisplay}
      brand={brand?.name}
      promotionIconVariant={
        promotionIconVariant ? promotionIconVariant.toString() : undefined
      }
      useSimplifiedVersion={useSimplifiedVersion}
      isMobile={isMobile}
    />
  );
};

const getColourReductionLabelInfo = (
  product: Product,
  variant: Variant | undefined,
  variantGroup: VariantGroup,
  showPromoOnIndicators: boolean,
  isAvailable: boolean,
) => {
  if (!isAvailable) {
    return {
      showReduced: false,
      label: "",
    };
  }

  const allVariantsReduced = isAllVariantsReduced(product);
  const allVariantsSave = isAllVariantsSave(product);

  if (allVariantsReduced || allVariantsSave) {
    return {
      showReduced: showPromoOnIndicators,
      label: showPromoOnIndicators
        ? allVariantsReduced
          ? "Reduced"
          : "Offer"
        : "",
    };
  }

  if (!hasSizes(product) && variant) {
    const isVariantReduced = isVariantReducedToClear(variant);
    const isVariantSave = isVariantPromotional(variant);

    return {
      showReduced: isVariantReduced || isVariantSave,
      label: isVariantReduced ? "Reduced" : isVariantSave ? "Offer" : "",
    };
  }

  const someVariantsReducedForColour =
    isSomeVariantsReducedForColour(variantGroup);

  const allVariantsReducedForColour =
    isAllVariantsReducedForColour(variantGroup);

  const someVariantsSaveForColour = isSomeVariantsSaveForColour(variantGroup);

  const allVariantsSaveForColour = isAllVariantsSaveForColour(variantGroup);

  if (someVariantsReducedForColour) {
    return { showReduced: true, label: "Selected sizes reduced" };
  }

  if (allVariantsReducedForColour) {
    return { showReduced: true, label: "Reduced" };
  }

  if (someVariantsSaveForColour) {
    return { showReduced: true, label: "Selected sizes offer" };
  }

  if (allVariantsSaveForColour) {
    return { showReduced: true, label: "Offer" };
  }

  return { showReduced: false, label: "" };
};

const getItemsCountToDisplay = (
  isMobile: boolean,
  colourOptions: ColourOption[],
  selectedVariant: Variant,
): number => {
  const selectedColourIndex = colourOptions.findIndex(
    (colour) => colour.colour === selectedVariant.differentiators?.colour,
  );

  const ceil = isMobile
    ? Math.ceil((selectedColourIndex + 1) / 2) * 2
    : Math.ceil((selectedColourIndex + 1) / 3) * 3;
  if (selectedColourIndex >= 9) return ceil;

  if (isMobile) return 10;

  return 9;
};

function sortColourOptions(colours: ColourOption[]) {
  const colourOptions = [...colours];

  const colourOptionsAvailable = colourOptions.filter(
    (colourOption) => colourOption.showAsAvailable,
  );

  const colourOptionsNotAvailable = colourOptions.filter(
    (colourOption) => !colourOption.showAsAvailable,
  );

  const combinedColourOptions = [
    ...colourOptionsAvailable,
    ...colourOptionsNotAvailable,
  ];

  return combinedColourOptions;
}

function getVariantsForGroup(variantGroup: VariantGroup, product: Product) {
  const groupVariantIds = variantGroup.variants.map((v) => v.id);
  const allVariants: Variant[] = product.variants;
  const groupVariants = allVariants.filter((v) =>
    groupVariantIds.includes(v.id),
  );
  return groupVariants;
}
